import ChatMessageCountField from "components/fields/ChatMessageCountField";
import CustomerField from "components/fields/CustomerField";
import { AppList } from "components/list/AppList";
import { FakeReferenceTextField } from "components/fields/FakeReferenceTextField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { TERMINAL_DEFAULT_VALUE } from "components/filters/ToggleTerminalsFilter";
import DeviationChat from "../Chat";
import { DeviationMainCategory } from "entities/DeviationMainCategory";
import { DeviationStatus, allowedEditRolesByStatus } from "entities/DeviationStatus";
import { DeviationType } from "entities/DeviationType";
import { UserRole } from "entities/UserRole";
import React from "react";
import {
	Datagrid,
	EditButton,
	ReferenceField,
	TextField,
	useGetList,
	usePermissions,
} from "react-admin";
import { ToggleDatesLaborForecastEnum } from "resources/labor-forecast/List/ToggleDatesFilter";

import {
	stylesForColorInheritInList,
	stylesNoShadowInList,
} from "../../../components/_helpers/useStyles";
import { DateTimeField } from "../../../components/fields/DateFormatField";
import { ScrollingWrapper } from "../../../components/ScrollingWrapper";
import { DeviationsActions } from "./Actions";
import { DeviationsFilter } from "./ListFilters";
import { ToggleDatesEnum } from "components/filters/ToggleDatesFilter";
import { useGridUserDataGlobalFiltersContext } from "contexts/GridUserDataGlobalFiltersProvider";

const defaultSort = { field: "id", order: "ASC" };
const defaultFilters = {
	named_created_at: [ToggleDatesEnum.ALL],
	status_state: "active",
};

const canCreate = new Set([
	UserRole.admin,
	UserRole.osfc_employee,
	UserRole.osfc_manager,
	UserRole.app_manager,
]);

const EditViewButton = ({ permissions, ...props }) => {
	const canEdit = allowedEditRolesByStatus[props.record.status]?.has(permissions?.role);
	const buttonProps = { label: canEdit ? "" : "ra.action.view" };
	if (!canEdit) buttonProps.icon = <React.Fragment />;
	return <EditButton {...props} {...buttonProps} />;
};


export const DeviationsList = (props) => {
	const classes = stylesForColorInheritInList();
	const classesNoShadow = stylesNoShadowInList();
	const { permissions } = usePermissions();
	const { ids: _, data: statuses } = useGetList("deviation-statuses");
	const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();

	const permanentFilter = {
		terminal_id: globalFilters?.terminal_id ?? [TERMINAL_DEFAULT_VALUE],
		...(props?.filterDefaultValues ?? {}),
	};
	const filterDefaults = props?.filterDefaultValues
		? permanentFilter
		: { ...defaultFilters, ...permanentFilter };

	const paginationProps = {};
	if (props.isOrderDeviationTab) {
		paginationProps.pagination = false;
	}
	const hasCreate = props.isOrderDeviationTab && canCreate.has(permissions?.role);

	const deviationsRowStyle = (record, index) => ({
		backgroundColor: statuses[record.status] && statuses[record.status].color,
	});

	return (
		<AppList
			{...props}
			sort={defaultSort}
			filter={permanentFilter}
			filterDefaultValues={filterDefaults}
			exporter={false}
			bulkActionButtons={false}
			filters={props.isOrderDeviationTab ? null : <DeviationsFilter />}
			{...paginationProps}
			classes={{ ...classes, ...classesNoShadow }}
			actions={<DeviationsActions />}
			hasCreate={hasCreate}
		>
			<ScrollingWrapper>
				<Datagrid
					rowStyle={deviationsRowStyle}
					// TODO: It doesn't work
					rowDoubleClick={(id, basePath, record) => `/deviations/${record.id}/show`}
				>
					<CustomerField source="company_id" formatLabel={(record) => record["customer_name"]} />
					<FakeReferenceTextField source="terminal_id" replaceSource="terminal_name" />
					<DateTimeField source="created_at" />
					<ReferenceField source="created_by" reference="users" link={false}>
						<TextField source="first_name" />
					</ReferenceField>
					<SelectEnumField
						source="deviation_main_category"
						enumName="deviation_main_category"
						enumObj={DeviationMainCategory}
					/>
					<SelectEnumField
						source="deviation_type"
						enumName="deviation_type"
						enumObj={DeviationType}
					/>
					<ReferenceField
						source="order_id"
						reference="orders"
						link={(record, reference) => `/orders/${record.order_id}/show`}
					>
						<TextField source="id" />
					</ReferenceField>
					<ReferenceField
						source="order_id"
						label={"resources.deviations.fields.order_reference"}
						reference="orders"
						link={(record, reference) => `/orders/${record.order_id}/show`}
					>
						<TextField source="reference" />
					</ReferenceField>
					<SelectEnumField source="status" enumName="deviation_status" enumObj={DeviationStatus} />
					<ChatMessageCountField
						source="message_count"
						label=""
						reorder_name="Messages"
						sortable={false}
						ChatComponent={DeviationChat}
					/>
					<EditViewButton permissions={permissions} />
				</Datagrid>
			</ScrollingWrapper>
		</AppList>
	);
};
